/**
 * Fork of `react-parallax-carousel@1.1.0` (MIT). Changes:
 * - Only the active slide carries `data-linked` / navigates; peek prev/next do not.
 * - Optional `arrowsViewportInsetPx`: renders nav buttons inside `.rpc-viewport` so they are not
 *   clipped by `overflow:hidden` peek cards (arrows align to carousel viewport edges, not slides).
 * - Ignores Chromium touch→mouse compatibility events so DevTools/mobile does not navigate twice.
 * - Optional `onActiveSlideNavigate`: called when the active slide is tapped to navigate (before `location` / `window.open`).
 * - Optional `onUserSlideChange`: when normalized active index changes (user, autoplay, arrows, etc.); skipped if index unchanged.
 * Re-copy from `node_modules/react-parallax-carousel/dist/index.esm.js` when upgrading the package.
 */
/* eslint-disable -- vendored upstream; keep diff small for upgrades */
import React, { useRef, useEffect } from 'react';

const STYLES = `
.rpc-root *,
.rpc-root *::before,
.rpc-root *::after {
  box-sizing: border-box;
}

.rpc-root {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  -webkit-user-select: none;
  user-select: none;
}

/* ── Header ── */
.rpc-header {
  text-align: center;
  margin-bottom: 36px;
  color: #fff;
}
.rpc-header h1 {
  margin: 0;
  font-size: clamp(1.3rem, 2.8vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  opacity: 0.92;
}
.rpc-header p {
  margin: 6px 0 0;
  font-size: 0.75rem;
  opacity: 0.32;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

/* ── Viewport ── */
.rpc-viewport {
  width: 100%;
  overflow: hidden;
  cursor: grab;
  position: relative;
}
.rpc-viewport.rpc-viewport--dragging {
  cursor: grabbing;
}

/* Overlay nav (optional): pinned to viewport edges, not slide boxes */
.rpc-viewport-arrow-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 25;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  padding: 0;
  margin: 0;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
}
.rpc-viewport-arrow-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

/* ── Track ── */
.rpc-track {
  display: flex;
  align-items: center;
  will-change: transform;
  /* gap + padding driven by inline style from JS config */
}

/* ── Card — overflow:hidden mask window ── */
.rpc-card {
  flex: 0 0 auto;
  /* height driven by --rpc-card-h on .rpc-root */
  height: var(--rpc-card-h, 78vh);
  border-radius: var(--rpc-radius, 24px);
  overflow: hidden;
  position: relative;
  cursor: pointer;
  box-shadow:
    0 6px 32px rgba(0, 0, 0, 0.48),
    0 2px 6px rgba(0, 0, 0, 0.3);
  /* Only box-shadow transitions via CSS; width/position are JS-driven */
  transition: box-shadow 0.45s ease;
}
.rpc-card.rpc-card--active {
  cursor: default;
  box-shadow:
    0 18px 56px rgba(0, 0, 0, 0.7),
    0 4px 14px rgba(0, 0, 0, 0.4);
}
/* Active card with a link is clickable */
.rpc-card.rpc-card--active[data-linked] {
  cursor: pointer;
}
/* Prev / next peek cards are nav targets */
.rpc-card.rpc-card--prev,
.rpc-card.rpc-card--next {
  cursor: pointer;
}

/*
  ── .rpc-card-img — the oversized floating image layer ──
  Width is set in JS as fixed px (activeCardWidth × 1.4).
  Using % here causes zoom as the card flex-basis animates.
  left: 50% + JS translate3d(calc(-50% + offset%), 0, 0) = centered + parallax.
*/
.rpc-card-img {
  position: absolute;
  top: 0;
  left: 50%;
  /* width set by JS — do NOT use % here */
  height: 100%;
  will-change: transform;
}
.rpc-card-img img {
  width: 98%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
  /* own compositing layer */
  transform: translate3d(0, 0, 0);
}

/* ── Nav arrows — shown on prev/next peek cards ── */
.rpc-arrow {
  display: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 3;
  pointer-events: none;
}
.rpc-card--prev .rpc-arrow--left,
.rpc-card--next .rpc-arrow--right {
  display: flex;
}

/* ── Label ── */
.rpc-card-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 48px 28px 26px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.74) 0%, transparent 100%);
  color: #fff;
  pointer-events: none;
  z-index: 2;
  transform-origin: bottom left;
  opacity: 1;
}

.rpc-tag {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.58;
  margin-bottom: 7px;
}
.rpc-card-label h2 {
  margin: 0;
  font-size: clamp(1rem, 2.4vw, 1.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
}
`;
const STYLE_ID = 'react-parallax-carousel-styles-v1';
function injectStyles() {
    if (typeof document === 'undefined')
        return; // SSR guard
    if (document.getElementById(STYLE_ID))
        return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
}

/**
 * ParallaxCarousel
 *
 * Spring-physics parallax carousel with asymmetric peek layout:
 * - Left side shows only the immediately adjacent card peeking in from the edge.
 * - Right side shows a larger (configurable) peek of the next card.
 * - All other non-adjacent cards collapse to zero width so they stay hidden.
 *
 * Control the layout with `peekLeftVw` and `peekRightVw`; everything else
 * (active width, inactive widths) is derived automatically.
 *
 * If the `slides` array changes length, remount via the `key` prop:
 *   `<ParallaxCarousel key={slides.length} slides={slides} />`
 */
const ParallaxCarousel = ({ slides = [], parallaxRange = 14, parallaxLerp = 0.1, stiffness = 0.022, damping = 0.58, maxVel = 12, widthLerp = 0.07, peekLeftVw = 10, peekRightVw = 18, cardHeight = '78vh', gap = '20px', borderRadius = '24px', title = 'Discover the World', subtitle = 'Drag to explore', showHeader = true, initialIndex = 0, loop = true, showArrows = true, arrowColor = 'rgba(255,255,255,0.9)', autoPlay = false, autoPlayDelay = 3000, linkTarget = '_blank', onChange, arrowsViewportInsetPx, onActiveSlideNavigate, onUserSlideChange, }) => {
    // Derive card widths from the peek props.
    // peekLeft + activeWidth + peekRight = 100vw (fills the viewport exactly).
    const activeWidthVw = 100 - peekLeftVw - peekRightVw;
    const inactiveWidthVw = peekLeftVw * 2;
    const nextWidthVw = peekRightVw * 2;
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const cardRefs = useRef([]);
    const imgRefs = useRef([]);
    const configRef = useRef({
        parallaxRange, parallaxLerp, stiffness, damping,
        maxVel, widthLerp, loop,
        activeWidthVw, inactiveWidthVw, nextWidthVw, peekLeftVw,
    });
    configRef.current = {
        parallaxRange, parallaxLerp, stiffness, damping,
        maxVel, widthLerp, loop,
        activeWidthVw, inactiveWidthVw, nextWidthVw, peekLeftVw,
    };
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const slidesRef = useRef(slides);
    slidesRef.current = slides;
    const autoPlayRef = useRef(autoPlay);
    autoPlayRef.current = autoPlay;
    const autoPlayDelayRef = useRef(autoPlayDelay);
    autoPlayDelayRef.current = autoPlayDelay;
    const linkTargetRef = useRef(linkTarget);
    linkTargetRef.current = linkTarget;
    const onActiveSlideNavigateRef = useRef(onActiveSlideNavigate);
    onActiveSlideNavigateRef.current = onActiveSlideNavigate;
    const onUserSlideChangeRef = useRef(onUserSlideChange);
    onUserSlideChangeRef.current = onUserSlideChange;
    const goToRef = useRef(() => { });
    const anim = useRef({
        activeIdx: 0,
        trackX: 0,
        snapTarget: 0,
        springVel: 0,
        dragging: false,
        didDrag: false,
        ptrStartX: 0,
        trkStartX: 0,
        ptrPrevX: 0,
        relVel: 0,
        ptrTapTarget: null,
        /** After a touch-originated tap, Chromium emits compatibility mouse events; ignore them. */
        compatMouseSuppressUntil: 0,
        gestureFromTouchUi: false,
        imgX: new Float64Array(0),
        imgXTgt: new Float64Array(0),
        cardW: new Float64Array(0),
        rafId: 0,
    });
    useEffect(() => {
        var _a;
        injectStyles();
        const viewport = viewportRef.current;
        const track = trackRef.current;
        const cards = cardRefs.current;
        const cardImgs = imgRefs.current;
        const a = anim.current;
        const n = slides.length;
        a.imgX = new Float64Array(n);
        a.imgXTgt = new Float64Array(n);
        a.cardW = new Float64Array(n);
        // ─── Helpers ─────────────────────────────────────────────────────────────
        function lerp(x, y, t) { return x + (y - x) * t; }
        function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
        function cfg() { return configRef.current; }
        function getActiveW() { return viewport.offsetWidth * (cfg().activeWidthVw / 100); }
        function getInactiveW() { return viewport.offsetWidth * (cfg().inactiveWidthVw / 100); }
        function getNextW() { return viewport.offsetWidth * (cfg().nextWidthVw / 100); }
        function getPeekLeftPx() { return viewport.offsetWidth * (cfg().peekLeftVw / 100); }
        // Resolve the gap prop to px once at mount. Supports plain px values ('20px').
        // For rem/vw etc. a fallback of 20 is used; override gap in px for accuracy.
        const gapPx = parseFloat(gap) || 20;
        function setImgWidths() {
            var _a;
            const fixedW = getActiveW() * 1.4;
            for (let i = 0; i < n; i++) {
                (_a = cardImgs[i]) === null || _a === void 0 ? void 0 : _a.style.setProperty('width', fixedW.toFixed(1) + 'px');
            }
        }
        // Snap the track so the active card's LEFT EDGE aligns with peekLeftVw.
        // This gives an asymmetric layout: left peek = peekLeftVw, right peek
        // fills the remaining viewport space (100 - peekLeftVw - activeWidthVw).
        function snapX(i) {
            const c = cards[i];
            if (!c)
                return 0;
            return -(c.offsetLeft - getPeekLeftPx());
        }
        // Returns the target flex-basis for card i given current activeIdx.
        // Cards further than 1 position away collapse to 0 so only the two
        // immediate neighbours (prev + next) can ever peek in from the edges.
        function targetWidth(i, ai) {
            if (i === ai)
                return getActiveW();
            if (i === ai + 1)
                return getNextW();
            if (i === ai - 1)
                return getInactiveW();
            return 0;
        }
        // Apply flex-basis + margin-right to a card.
        // We use per-card margin-right instead of a track gap so that collapsed
        // (0-width) cards contribute exactly 0 space — if we used CSS gap, each
        // collapsed card would still push cards apart by `gap` px.
        function applyCardWidth(i, w) {
            const card = cards[i];
            if (!card)
                return;
            card.style.flexBasis = w.toFixed(1) + 'px';
            card.style.marginRight = w < 0.5 ? '0px' : `${gapPx}px`;
        }
        function syncCards() {
            const curSlides = slidesRef.current;
            for (let i = 0; i < n; i++) {
                const card = cards[i];
                if (!card)
                    continue;
                card.classList.toggle('rpc-card--active', i === a.activeIdx);
                card.classList.toggle('rpc-card--prev', i === a.activeIdx - 1);
                card.classList.toggle('rpc-card--next', i === a.activeIdx + 1);
                const href = curSlides[i]?.href;
                if (i === a.activeIdx && href) {
                    card.setAttribute('data-linked', 'true');
                } else {
                    card.removeAttribute('data-linked');
                }
            }
        }
        function goTo(i, _isProgrammaticAdvance = false) {
            var _a;
            if (configRef.current.loop) {
                i = ((i % n) + n) % n;
            }
            else {
                i = clamp(i, 0, n - 1);
            }
            const previousActiveIdx = a.activeIdx;
            a.activeIdx = i;
            a.snapTarget = snapX(a.activeIdx);
            if (Math.abs(a.trackX - a.snapTarget) < 1)
                a.springVel = 0;
            syncCards();
            (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, a.activeIdx);
            if (a.activeIdx !== previousActiveIdx) {
                var ucb = onUserSlideChangeRef.current;
                if (typeof ucb === 'function') {
                    try {
                        ucb(a.activeIdx);
                    }
                    catch (err) {
                        console.error('ParallaxCarousel onUserSlideChange:', err);
                    }
                }
            }
            scheduleAutoPlay();
        }
        goToRef.current = goTo;
        function snapNearest() {
            let best = 0, bestD = Infinity;
            for (let i = 0; i < n; i++) {
                const d = Math.abs(a.trackX - snapX(i));
                if (d < bestD) {
                    bestD = d;
                    best = i;
                }
            }
            goTo(best);
        }
        // ─── Auto-play ────────────────────────────────────────────────────────────
        let autoPlayTimeout = null;
        function clearAutoPlay() {
            if (autoPlayTimeout != null) {
                clearTimeout(autoPlayTimeout);
                autoPlayTimeout = null;
            }
        }
        function scheduleAutoPlay() {
            clearAutoPlay();
            if (!autoPlayRef.current)
                return;
            autoPlayTimeout = setTimeout(() => {
                if (autoPlayRef.current && !a.dragging) {
                    goTo(a.activeIdx + 1, true);
                }
                else {
                    scheduleAutoPlay();
                }
            }, autoPlayDelayRef.current);
        }
        // ─── Parallax ──────────────────────────────────────────────────────────
        function updateParallaxTargets() {
            const vpW = viewport.offsetWidth;
            const vpMid = vpW / 2;
            const range = cfg().parallaxRange;
            for (let i = 0; i < n; i++) {
                const card = cards[i];
                if (!card)
                    continue;
                const r = card.getBoundingClientRect();
                const cMid = r.left + r.width / 2;
                const prog = (cMid - vpMid) / vpW;
                a.imgXTgt[i] = clamp(prog * -range, -18, 18);
            }
        }
        // ─── RAF Loop ────────────────────────────────────────────────────────────
        function tick() {
            a.rafId = requestAnimationFrame(tick);
            const { stiffness: K, damping: D, widthLerp: WL, parallaxLerp: PL } = cfg();
            // 1. Spring — re-compute snap target every frame so it tracks card
            //    layout as widths animate.
            if (!a.dragging) {
                a.snapTarget = snapX(a.activeIdx);
                const force = (a.snapTarget - a.trackX) * K;
                a.springVel = (a.springVel + force) * D;
                a.trackX += a.springVel;
            }
            track.style.transform = 'translate3d(' + a.trackX.toFixed(2) + 'px, 0, 0)';
            // 2. Card-width animation — only the active card and its two immediate
            //    neighbours get a non-zero width; everything else lerps to 0.
            for (let i = 0; i < n; i++) {
                const target = targetWidth(i, a.activeIdx);
                a.cardW[i] = lerp(a.cardW[i], target, WL);
                applyCardWidth(i, a.cardW[i]);
            }
            // 3. Parallax
            updateParallaxTargets();
            for (let i = 0; i < n; i++) {
                const img = cardImgs[i];
                if (!img)
                    continue;
                a.imgX[i] = lerp(a.imgX[i], a.imgXTgt[i], PL);
                img.style.transform =
                    'translate3d(calc(-50% + ' + a.imgX[i].toFixed(3) + '%), 0, 0)';
            }
        }
        // ─── Pointer Events ───────────────────────────────────────────────────────
        function ptrX(e) {
            return e.touches
                ? e.touches[0].clientX
                : e.clientX;
        }
        function onDown(e) {
            if (e.button && e.button !== 0)
                return;
            /* Chrome (incl. DevTools mobile): touch -> synthetic mouse; second "click" would open href after goTo. */
            const blockDuplicateMouse =
                typeof MouseEvent !== 'undefined' &&
                e instanceof MouseEvent &&
                (e.sourceCapabilities?.firesTouchEvents === true ||
                    (typeof performance !== 'undefined' &&
                        (a.compatMouseSuppressUntil ?? 0) > performance.now()));
            if (blockDuplicateMouse)
                return;
            a.gestureFromTouchUi = e.type === 'touchstart';
            const t = e.target;
            a.ptrTapTarget =
                t instanceof Element ? t : t && 'parentElement' in t && t.parentElement instanceof Element ? t.parentElement : null;
            a.springVel = 0;
            a.dragging = true;
            a.didDrag = false;
            a.ptrStartX = ptrX(e);
            a.trkStartX = a.trackX;
            a.ptrPrevX = a.ptrStartX;
            a.relVel = 0;
            viewport.classList.add('rpc-viewport--dragging');
            clearAutoPlay();
        }
        function onMove(e) {
            if (!a.dragging)
                return;
            const x = ptrX(e);
            const dx = x - a.ptrStartX;
            if (!a.didDrag && Math.abs(dx) > 4)
                a.didDrag = true;
            if (a.didDrag) {
                a.relVel = x - a.ptrPrevX;
                a.ptrPrevX = x;
                a.trackX = a.snapTarget = a.trkStartX + dx;
            }
        }
        function onUp(e) {
            var _a;
            if (!a.dragging)
                return;
            a.dragging = false;
            viewport.classList.remove('rpc-viewport--dragging');
            try {
                if (!a.didDrag) {
                    const te = e;
                    const clientX = te.changedTouches ? te.changedTouches[0].clientX : e.clientX;
                    const clientY = te.changedTouches ? te.changedTouches[0].clientY : e.clientY;
                    const fromDownCard = (() => {
                        const el = a.ptrTapTarget;
                        if (!(el instanceof Element) || !viewport.contains(el))
                            return null;
                        return el.closest('.rpc-card');
                    })();
                    const hit = document.elementFromPoint(clientX, clientY);
                    const fromPointCard = (hit instanceof Element ? hit : null)?.closest('.rpc-card') ??
                        null;
                    const tapped = fromDownCard && viewport.contains(fromDownCard)
                        ? fromDownCard
                        : fromPointCard && viewport.contains(fromPointCard)
                          ? fromPointCard
                          : null;
                    if (tapped) {
                        const idx = cards.indexOf(tapped);
                        if (idx !== -1) {
                            if (idx !== a.activeIdx) {
                                goTo(idx);
                            }
                            else {
                                if (tapped.classList.contains('rpc-card--active')) {
                                    const slide = slidesRef.current[idx];
                                    const href = slide === null || slide === void 0 ? void 0 : slide.href;
                                    if (href) {
                                        const cb = onActiveSlideNavigateRef.current;
                                        let handled = false;
                                        if (typeof cb === 'function') {
                                            try {
                                                handled = cb(slide, idx) === true;
                                            }
                                            catch (err) {
                                                console.error('ParallaxCarousel onActiveSlideNavigate:', err);
                                            }
                                        }
                                        if (!handled) {
                                            const target = linkTargetRef.current;
                                            if (target === '_self') {
                                                window.location.href = href;
                                            }
                                            else {
                                                window.open(href, target, 'noopener,noreferrer');
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    scheduleAutoPlay();
                    if (a.gestureFromTouchUi && typeof performance !== 'undefined') {
                        a.compatMouseSuppressUntil = performance.now() + 650;
                    }
                    return;
                }
                a.springVel = clamp(a.relVel, -cfg().maxVel, cfg().maxVel);
                snapNearest();
            }
            finally {
                a.ptrTapTarget = null;
                a.gestureFromTouchUi = false;
            }
        }
        function onContextMenu(e) { if (a.dragging)
            e.preventDefault(); }
        function onKeyDown(e) {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown')
                goTo(a.activeIdx + 1);
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
                goTo(a.activeIdx - 1);
        }
        // ─── Resize ───────────────────────────────────────────────────────────────
        let resizeTimer;
        function onResize() {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                a.trackX = a.snapTarget = snapX(a.activeIdx);
                a.springVel = 0;
                setImgWidths();
                for (let i = 0; i < n; i++) {
                    const w = targetWidth(i, a.activeIdx);
                    a.cardW[i] = w;
                    applyCardWidth(i, w);
                }
            }, 100);
        }
        // ─── Init ─────────────────────────────────────────────────────────────────
        a.activeIdx = Math.max(0, Math.min(initialIndex, n - 1));
        syncCards();
        for (let i = 0; i < n; i++) {
            const w = targetWidth(i, a.activeIdx);
            a.cardW[i] = w;
            applyCardWidth(i, w);
        }
        setImgWidths();
        for (let i = 0; i < n; i++) {
            (_a = cardImgs[i]) === null || _a === void 0 ? void 0 : _a.style.setProperty('transform', 'translate3d(-50%, 0, 0)');
        }
        a.trackX = a.snapTarget = snapX(a.activeIdx);
        track.style.transform = 'translate3d(' + a.trackX.toFixed(2) + 'px, 0, 0)';
        const _onDown = onDown;
        const _onMove = onMove;
        const _onUp = onUp;
        tick();
        scheduleAutoPlay();
        viewport.addEventListener('mousedown', _onDown);
        viewport.addEventListener('touchstart', _onDown, { passive: true });
        window.addEventListener('mousemove', _onMove);
        window.addEventListener('touchmove', _onMove, { passive: true });
        window.addEventListener('mouseup', _onUp);
        window.addEventListener('touchend', _onUp, { passive: true });
        viewport.addEventListener('contextmenu', onContextMenu);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onResize);
        return () => {
            cancelAnimationFrame(a.rafId);
            clearAutoPlay();
            clearTimeout(resizeTimer);
            viewport.removeEventListener('mousedown', _onDown);
            viewport.removeEventListener('touchstart', _onDown);
            window.removeEventListener('mousemove', _onMove);
            window.removeEventListener('touchmove', _onMove);
            window.removeEventListener('mouseup', _onUp);
            window.removeEventListener('touchend', _onUp);
            viewport.removeEventListener('contextmenu', onContextMenu);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', onResize);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Track padding:
    //   left  = peekLeftVw  → reserve space for the left peek
    //   right = peekRightVw → reserve space for the right peek (also ensures
    //           the last card can scroll into its snap position)
    const trackPadding = `0 ${peekRightVw}vw 0 ${peekLeftVw}vw`;
    const overlayInsetPx = arrowsViewportInsetPx;
    const useViewportOverlayArrows = typeof overlayInsetPx === 'number' &&
        overlayInsetPx >= 0 &&
        showArrows &&
        slides.length > 1;
    const stopOverlayFromStartingDrag = (e) => {
        e.stopPropagation();
    };
    const viewportArrowSvg = (points) => (React.createElement("svg", { width: "28", height: "28", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round" },
        React.createElement("polyline", { points: points })));
    return React.createElement(
        "div",
        {
            className: "rpc-root",
            style: {
                "--rpc-card-h": cardHeight,
                "--rpc-radius": borderRadius,
            },
        },
        showHeader && (title || subtitle) &&
            React.createElement(
                "header",
                { className: "rpc-header" },
                title && React.createElement("h1", null, title),
                subtitle && React.createElement("p", null, subtitle),
            ),
        React.createElement(
            "div",
            { ref: viewportRef, className: "rpc-viewport" },
            React.createElement(
                "div",
                { ref: trackRef, className: "rpc-track", style: { padding: trackPadding } },
                slides.map((slide, i) =>
                    React.createElement(
                        "div",
                        {
                            key: slide.id != null ? slide.id : i,
                            ref: (el) => {
                                cardRefs.current[i] = el;
                            },
                            className: "rpc-card",
                        },
                        React.createElement(
                            "div",
                            {
                                ref: (el) => {
                                    imgRefs.current[i] = el;
                                },
                                className: "rpc-card-img",
                            },
                            React.createElement("img", {
                                src: slide.image,
                                alt: slide.alt || slide.title || "",
                                draggable: false,
                            }),
                        ),
                        (slide.tag || slide.title) &&
                            React.createElement(
                                "div",
                                { className: "rpc-card-label" },
                                slide.tag && React.createElement("span", { className: "rpc-tag" }, slide.tag),
                                slide.title && React.createElement("h2", null, slide.title),
                            ),
                        showArrows &&
                            !useViewportOverlayArrows &&
                            React.createElement(
                                React.Fragment,
                                null,
                                React.createElement(
                                    "div",
                                    {
                                        className: "rpc-arrow rpc-arrow--left",
                                        style: { color: arrowColor },
                                        "aria-hidden": "true",
                                    },
                                    viewportArrowSvg("15 18 9 12 15 6"),
                                ),
                                React.createElement(
                                    "div",
                                    {
                                        className: "rpc-arrow rpc-arrow--right",
                                        style: { color: arrowColor },
                                        "aria-hidden": "true",
                                    },
                                    viewportArrowSvg("9 18 15 12 9 6"),
                                ),
                            ),
                    ),
                ),
            ),
            useViewportOverlayArrows &&
                React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(
                        "button",
                        {
                            type: "button",
                            className: "rpc-viewport-arrow-btn",
                            "aria-label": "Previous slide",
                            style: { color: arrowColor, left: overlayInsetPx, right: "auto" },
                            onPointerDownCapture: stopOverlayFromStartingDrag,
                            onMouseDownCapture: stopOverlayFromStartingDrag,
                            onTouchStartCapture: stopOverlayFromStartingDrag,
                            onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const g = goToRef.current;
                                if (typeof g === "function")
                                    g(anim.current.activeIdx - 1);
                            },
                        },
                        viewportArrowSvg("15 18 9 12 15 6"),
                    ),
                    React.createElement(
                        "button",
                        {
                            type: "button",
                            className: "rpc-viewport-arrow-btn",
                            "aria-label": "Next slide",
                            style: { color: arrowColor, right: overlayInsetPx, left: "auto" },
                            onPointerDownCapture: stopOverlayFromStartingDrag,
                            onMouseDownCapture: stopOverlayFromStartingDrag,
                            onTouchStartCapture: stopOverlayFromStartingDrag,
                            onClick: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const g = goToRef.current;
                                if (typeof g === "function")
                                    g(anim.current.activeIdx + 1);
                            },
                        },
                        viewportArrowSvg("9 18 15 12 9 6"),
                    ),
                ),
        ),
    );
};

export { ParallaxCarousel };
//# sourceMappingURL=index.esm.js.map
